import type { ActivationPendingUserResponse } from '@acme/api/admin';
import { UserService as AdminUserService } from '@acme/api/admin';
import type { PersonalInfoFormData, UpdateProfileModel } from '@acme/domains';
import { OrganizationGeneralForm, UserGeneralForm } from '@acme/features';
import { Breadcrumb } from '@acme/foundation';
import { UserChecks } from '@widgets/userChecks';
import { UserHistory } from '@widgets/userHistory';
import { Badge, Button, Card, Spinner } from 'flowbite-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

type LocationState = {
  user?: ActivationPendingUserResponse;
};

const toFormDefaultData = (
  user: ActivationPendingUserResponse | null
): { person: { name: string } } => ({
  person: { name: user?.name ?? '' },
});

export function UserDetailsPage() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const location = useLocation();
  const state = (location.state as LocationState | null) ?? null;

  const [user, setUser] = useState<ActivationPendingUserResponse | null>(
    state?.user ?? null
  );
  const [isLoading, setIsLoading] = useState(false);

  const formRef = useRef<{ submit: () => Promise<void> }>(null);

  useEffect(() => {
    if (!id) return;
    if (user?.id === id) return;

    let isActive = true;
    (async () => {
      setIsLoading(true);
      try {
        const response = await AdminUserService.getApiAdminV1UserPending();
        const found = response.data?.find((u) => u.id === id) ?? null;
        if (isActive) setUser(found);
      } finally {
        if (isActive) setIsLoading(false);
      }
    })();

    return () => {
      isActive = false;
    };
  }, [id]);

  const updateProfile: UpdateProfileModel = async (
    _data: PersonalInfoFormData
  ) => {
    void _data;
    throw new Error(
      'Редактирование пользователя в админке пока не поддерживается'
    );
  };

  const breadcrumbItems = useMemo(
    () => [
      { label: 'Пользователи', onClick: () => navigate('/users/pending') },
      { label: user?.name ?? 'Детали пользователя' },
    ],
    [navigate, user?.name]
  );

  return (
    <div className="flex flex-col gap-6">
      <Breadcrumb items={breadcrumbItems} />

      <Card>
        <div className="flex items-start justify-between gap-6">
          <div className="flex flex-col gap-3">
            <div className="flex items-center gap-3">
              <h1 className="text-2xl font-semibold text-gray-900">
                Пользователь
              </h1>
              <Badge color="warning">Новый</Badge>
            </div>
          </div>

          <Button color="purple" outline disabled>
            Редактировать
          </Button>
        </div>

        <div className="my-6 border-t border-gray-200" />

        {isLoading ? (
          <div className="flex items-center justify-center py-10">
            <Spinner size="lg" />
          </div>
        ) : (
          <>
            <UserGeneralForm
              isEdit={false}
              defaultData={toFormDefaultData(user)}
              formRef={formRef}
              updateProfile={updateProfile}
            />
          </>
        )}
      </Card>
      <div>
        <OrganizationGeneralForm isEdit={false} defaultData={null} />
      </div>
      <UserChecks />
      <UserHistory />
    </div>
  );
}
